import { ReactNode, useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, Modal } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { useListMemberSeparableAccumulationsQuery } from "@/App/api/member";
import { useAccumulations } from "@/App/hooks/accumulations";
import { AccumulatorCard } from "@/App/pages/PlanContributions/Accumulations/AccumulatorCard";
import { DisplayNetworkStatuses, DisplayScopes } from "@/App/types/accumulation";

export interface AccumControllerProps {
  readonly displayNetworkStatus: DisplayNetworkStatuses;
  readonly displayScope: DisplayScopes;
  readonly isWidget: boolean;
}
export const Accumulations = ({ displayNetworkStatus, displayScope }: AccumControllerProps) => {
  const { t: translate } = useTranslation();
  const { data: accumulations, isLoading: isLoadingAccumulations } = useListMemberSeparableAccumulationsQuery(true);
  const {
    inNetworkAccumulations,
    outOfNetworkAccumulations,
    outOfPocketAccumulations,
    individualAccumulations,
    familyAccumulations,
    deductibleAccumulations,
    planMaxAccumulations,
  } = useAccumulations();

  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
  });

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalContent, setModalContent] = useState<string | ReactNode>("");

  const accumulationsToDisplay = useMemo(() => {
    if (!isLoadingAccumulations) {
      if (displayScope === DisplayScopes.Individual) {
        if (displayNetworkStatus === DisplayNetworkStatuses.In) {
          return individualAccumulations(inNetworkAccumulations(accumulations.results));
        } else if (displayNetworkStatus === DisplayNetworkStatuses.Out) {
          return individualAccumulations(outOfNetworkAccumulations(accumulations.results));
        }
      } else if (displayScope === DisplayScopes.Family) {
        if (displayNetworkStatus === DisplayNetworkStatuses.In) {
          return familyAccumulations(inNetworkAccumulations(accumulations.results));
        } else if (displayNetworkStatus === DisplayNetworkStatuses.Out) {
          return familyAccumulations(outOfNetworkAccumulations(accumulations.results));
        }
      }
    }
  }, [
    inNetworkAccumulations,
    outOfNetworkAccumulations,
    individualAccumulations,
    familyAccumulations,
    displayNetworkStatus,
    displayScope,
    accumulations,
  ]);

  const deductibleAccums = useMemo(() => {
    return deductibleAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, deductibleAccumulations]);

  const outOfPocketAccums = useMemo(() => {
    return outOfPocketAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, outOfPocketAccumulations]);

  const planMaxAccums = useMemo(() => {
    return planMaxAccumulations(accumulationsToDisplay);
  }, [accumulationsToDisplay, planMaxAccumulations]);

  const modalCloseHandler = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <Modal.Confirm visible={showModal} title={modalTitle} onClose={modalCloseHandler}>
        {modalContent}
      </Modal.Confirm>
      <Stack spacing="medium">
        {!!deductibleAccums?.length && (
          <>
            <Shelf spacing="mini">
              <Heading>{translate("BENEFITS.commercialBenefits.deductibles")}</Heading>
              <Button
                appearance="text"
                icon="question-circle"
                onClick={() => {
                  setModalTitle(translate("BENEFITS.commercialBenefits.deductibles"));
                  setModalContent(
                    <Trans
                      i18nKey="BENEFITS.commercialBenefits.deductiblesModalContent"
                      components={{
                        p: <p />,
                      }}
                    />,
                  );
                  setShowModal(true);
                }}
              />
              <Shelf.Spacer />
            </Shelf>
            <Grid spacing="medium">
              {deductibleAccums.map((accum) => {
                return (
                  <Grid.Span columns={columns}>
                    <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                  </Grid.Span>
                );
              })}
            </Grid>
          </>
        )}
        {!!outOfPocketAccums?.length && (
          <>
            <Shelf spacing="mini">
              <Heading>{translate("BENEFITS.commercialBenefits.outOfPockets")}</Heading>
              <Button
                appearance="text"
                icon="question-circle"
                onClick={() => {
                  setModalTitle(translate("BENEFITS.commercialBenefits.outOfPockets"));
                  setModalContent(
                    <Trans
                      i18nKey="BENEFITS.commercialBenefits.outOfPocketsModalContent"
                      components={{
                        p: <p />,
                      }}
                    />,
                  );
                  setShowModal(true);
                }}
              />
              <Shelf.Spacer />
            </Shelf>
            <Grid spacing="medium">
              {outOfPocketAccums.map((accum) => {
                return (
                  <Grid.Span columns={columns}>
                    <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                  </Grid.Span>
                );
              })}
            </Grid>
          </>
        )}
        {!!planMaxAccums?.length && (
          <>
            <Shelf spacing="mini">
              <Heading>{translate("BENEFITS.commercialBenefits.maximums")}</Heading>
              <Button
                appearance="text"
                icon="question-circle"
                onClick={() => {
                  setModalTitle(translate("BENEFITS.commercialBenefits.maximums"));
                  setModalContent(
                    <Trans
                      i18nKey="BENEFITS.commercialBenefits.maximumsModalContent"
                      components={{
                        p: <p />,
                      }}
                    />,
                  );
                  setShowModal(true);
                }}
              />
              <Shelf.Spacer />
            </Shelf>
            <Grid spacing="medium">
              {planMaxAccums.map((accum) => {
                return (
                  <Grid.Span columns={columns}>
                    <AccumulatorCard displayScope={displayScope} accumulator={accum} />
                  </Grid.Span>
                );
              })}
            </Grid>
          </>
        )}
      </Stack>
    </>
  );
};
